import { Injectable } from '@angular/core';

export interface ColorScheme {
  label: string;
  badgeColors: string[];
  palette: string[];
}

@Injectable({
  providedIn: 'root',
})
export class IrisColorService {

  public colors = {
    '$white':                    '#FFFFFF',
    '$primaryDarkBlue':          '#006099',
    '$primaryBlue':              '#0C80B6',
    '$primaryBlueLight':         '#56ACD4',
    '$primaryBluePale':          '#9ACDE5',
    '$primaryBlueBackground':    '#CCE6F2',
    '$secondaryGrey600':         '#17181A',
    '$secondaryGrey500':         '#34373A',
    '$secondaryGrey450':         '#484B4E',
    '$secondaryGrey400':         '#62676D',
    '$grey300':                  '#A4A8AC',
    '$grey250Disabled':          '#BFC2C5',
    '$grey150Borders':           '#E2E2E4',
    '$grey200':                  '#D5D6D9',
    '$grey100':                  '#F2F3F3',
    '$grey50Background':         '#F8F9FA',
    '$errorDark':                '#710000',
    '$error':                    '#A01D15',
    '$errorLight':               '#F4D2D0',
    '$warningDark':              '#CF8C09',
    '$warning':                  '#F2B819',
    '$warningLight':             '#FBEABA',
    '$accentRed':                '#D72622',
    '$successDark':              '#007B40',
    '$success':                  '#28AB6C',
    '$successLight':             '#D4EEE2',
  };

  public presetColorsForColorPicker = ['#F74723', '#FF7C0B', '#F4D225', '#7BC25D', '#00BD78', '#45C0D6', '#2582F4', '#3C57FA', '#AF4DFF', '#F04391'];
  public irisColorPickerPreset = [
    ['#F74723', '#FF7C0B', '#F4D225', '#7BC25D', '#2EB382', '#45C0D6', '#2582F4', '#3C57FA', '#AF4DFF', '#F04391'],
    ['#FAE8E5', '#FAEDE1', '#FAF7E6', '#F2F9EF', '#E5F9F2', '#ECF8FA', '#E5EEFA', '#E7EAFA', '#F2E7FA', '#FAE9F1'],
    ['#FDD1C8', '#FFDDC2', '#FCF4C8', '#DEEFD6', '#BFEDDD', '#D0EFF4', '#C8E0FC', '#CED5FF', '#E7CDFF', '#FBCFE3'],
    ['#FCA492', '#FFBD86', '#FAE993', '#C3E7B3', '#80DFBB', '#A3E0EB', '#93C1FA', '#9EACFD', '#D39CFF', '#F8A2C8'],
    ['#F9755A', '#FF9D48', '#F7DD5C', '#9CD286', '#40CE9A', '#74D0E1', '#5CA2F7', '#6E80FC', '#BC6BFF', '#F472AD'],
    ['#D23C1D', '#D9690A', '#CFB31E', '#69A54F', '#00A166', '#3BA3B6', '#1E70CF', '#334AD4', '#8C2FD9', '#CC387B'],
    ['#7C2312', '#803E05', '#7B6913', '#3E612F', '#005F3C', '#23606B', '#13417A', '#1E2B7D', '#531B80', '#782149'],
    ['#FFFFFF', '#FAFAFA', '#F0F1F2', '#E4E4E6', '#BEC0C2', '#969CA3', '#6B6F73', '#4B4E52', '#2D3947', '#171D24'],
  ];

  public readonly chartColorScheme = [
    '#F74723', '#FF7C0B', '#F4D225', '#7BC25D', '#00BD78', '#45C0D6', '#2582F4', '#3C57FA', '#AF4DFF', '#F04391',
    '#607D8B', '#7C2312', '#6200EA', '#388E3C', '#E35D5D', '#AFB42B', '#BEC0C2', '#F2B275', '#827717', '#0277BD',
    '#FA78C4', '#64DD17', '#D50000', '#AA00FF', '#AFB42B', '#006064', '#FFBF17', '#3D57AF', '#F25804', '#26BB92',
  ];

  public readonly colorSchemes: ColorScheme[] = [
    {
      label: 'label.dashboards.IrisTheme',
      badgeColors: ['#2DCE93', '#2D3947'],
      palette: ['#2DCE93', '#00B99B', '#00A39D', '#008D97', '#00778B', '#166178', '#2A4C61', '#2D3947'],
    },
    {
      label: 'label.dashboards.StrabagTheme',
      badgeColors: ['#D72621', '#A52420'],
      palette: ['#D72621', '#B92521', '#A52420', '#912320', '#7B221F', '#67211F', '#54201F', '#3E1F1E'],
    },
    {
      label: 'label.dashboards.GreenBlueTheme',
      badgeColors: ['#45C0D6', '#2EB382'],
      palette: ['#1DB37C', '#519DE9', '#5CC1E7', '#88E6E0', '#54E0AD'],
    },
    {
      label: 'label.dashboards.OrangeBlueTheme',
      badgeColors: ['#F88A5B', '#276D7E'],
      palette: ['#EF4A46', '#F05E3D', '#F88A5B', '#D58E6F', '#79A4AE', '#276D7E', '#134956', '#3C4856'],
    },
    {
      label: 'label.dashboards.RainbowTheme',
      badgeColors: ['#F4D14E', '#FB6362'],
      palette: ['#1DB8AA', '#98C86E', '#C8C46E', '#F4D14E', '#F6B952', '#F6A356', '#F97D5D', '#FB6362'],
    },
  ];

  constructor() { }

  getColorSchemeByName(colorSchemeName: string): ColorScheme {
    return this.colorSchemes.find(colorScheme => colorSchemeName === colorScheme.label);
  }

  hexToRgbaWithAlpha(hex: string, alpha: number): string {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? `rgba(
      ${parseInt(result[1], 16)},
      ${parseInt(result[2], 16)},
      ${parseInt(result[3], 16)},
      ${alpha}
    )` : null;
  }

  /**
   *  Input:
   *    rgba(255,25,2,0.5)
   *
   *  Output:
   *    #rrggbb or #rrggbbaa
   */
  rgbaToHexA(rgba: string, includeAlpha = false): string {
    const sep = rgba.indexOf(',') > -1 ? ',' : ' ';
    // Turn "rgba(r,g,b,a)" into [r,g,b,a]
    const rgbaChannels: string[] = rgba.slice(5).split(')')[0].split(sep);

    let r = (+rgbaChannels[0]).toString(16),
      g = (+rgbaChannels[1]).toString(16),
      b = (+rgbaChannels[2]).toString(16),
      a = Math.round(+rgbaChannels[3] * 255).toString(16);

    if (r.length == 1) {
      r = '0' + r;
    }
    if (g.length == 1) {
      g = '0' + g;
    }
    if (b.length == 1) {
      b = '0' + b;
    }

    if (includeAlpha) {
      if (a.length == 1) {
        a = '0' + a;
      }
      return '#' + r + g + b + a;
    }

    return '#' + r + g + b;
  }

  RGBtoHSB(r: number, g: number, b: number): [number, number, number] {
    const min = Math.min(r, g, b);
    const max = Math.max(r, g, b);

    const S = max !== 0 ? (max - min) / max : 0;
    let H = NaN;

    // min == max, means all components are the same
    // and the color is a shade of gray with no hue (H is NaN)
    if (min !== max) {
      const delta = max - min;
      const rc = (max - r) / delta;
      const gc = (max - g) / delta;
      const bc = (max - b) / delta;
      if (r === max) {
        H = bc - gc;
      } else if (g === max) {
        H = 2.0 + rc - bc;
      } else {
        H = 4.0 + gc - rc;
      }
      H /= 6.0;
      if (H < 0) {
        H = H + 1.0;
      }
    }

    return [H * 360, S, max];
  }

  HSBtoRGB(H: number, S: number, B: number, alpha = 1): { r: number; g: number; b: number; alpha: number } {
    if (isNaN(H)) {
      H = 0;
    }
    H = (((H % 360) + 360) % 360) / 360; // normalize hue to [0, 360] interval, then scale to [0, 1]

    let r = 0;
    let g = 0;
    let b = 0;

    if (S === 0) {
      r = g = b = B;
    } else {
      const h = (H - Math.floor(H)) * 6;
      const f = h - Math.floor(h);
      const p = B * (1 - S);
      const q = B * (1 - S * f);
      const t = B * (1 - (S * (1 - f)));
      switch (h >> 0) { // discard the floating point part of the number
        case 0:
          r = B;
          g = t;
          b = p;
          break;
        case 1:
          r = q;
          g = B;
          b = p;
          break;
        case 2:
          r = p;
          g = B;
          b = t;
          break;
        case 3:
          r = p;
          g = q;
          b = B;
          break;
        case 4:
          r = t;
          g = p;
          b = B;
          break;
        case 5:
          r = B;
          g = p;
          b = q;
          break;
      }
    }

    return {
      r: Math.min(1, Math.max(0, r || 0)),
      g: Math.min(1, Math.max(0, g || 0)),
      b: Math.min(1, Math.max(0, b || 0)),
      alpha: Math.min(1, Math.max(0, alpha || 0)),
    };
  }

  toHexString(r: number, g: number, b: number, a: number): string {
    let hex = '#'
      + this.padHex(Math.round(r * 255).toString(16))
      + this.padHex(Math.round(g * 255).toString(16))
      + this.padHex(Math.round(b * 255).toString(16));

    if (a < 1) {
      hex += this.padHex(Math.round(a * 255).toString(16));
    }

    return hex;
  }

  fromHexString(str: string): { r: number; g: number; b: number; alpha: number } {
    const values = this.parseHex(str);
    if (values) {
      const [r, g, b, a] = values;
      const rgba = {
        r: r / 255,
        g: g / 255,
        b: b / 255,
        alpha: a / 255,
      };

      return rgba;
      // return new Color(r / 255, g / 255, b / 255, a / 255);
    }

    throw new Error(`Malformed hexadecimal color string: '${str}'`);
  }

  private parseHex(input: string): number[] | undefined {
    input = input.replace(/ /g, '').slice(1);
    let parts: number[];

    switch (input.length) {
      case 6:
      case 8:
        parts = [];
        for (let i = 0; i < input.length; i += 2) {
          parts.push(parseInt(`${input[i]}${input[i + 1]}`, 16));
        }
        break;
      case 3:
      case 4:
        parts = input.split('').map(p => parseInt(p, 16)).map(p => p + p * 16);
        break;
    }

    if (parts.length >= 3) {
      if (parts.every((p: number) => p >= 0)) {
        if (parts.length === 3) {
          parts.push(255);
        }
        return parts;
      }
    }
  }

  private padHex(str: string): string {
    return str.length === 1 ? '0' + str : str;
  }

}
